import { Capacitor } from "@capacitor/core";
import api from "@/lib/api";

export const isNative = () => Capacitor.isNativePlatform();
export const nativePlatform = () => Capacitor.getPlatform();
export const APP_SCHEME = "com.dividendportfolioplanner.com";

const randomStr = () => Array.from(crypto.getRandomValues(new Uint8Array(24)), (b) => b.toString(16).padStart(2, "0")).join("");

// Google via Emergent auth in a system browser sheet; the web bounce page deep-links back into the app.
export async function nativeGoogleSignIn() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // In the app shell window.location.origin is capacitor://localhost, so the live web origin comes from the build env.
  const redirectUrl = process.env.REACT_APP_BACKEND_URL + "/auth/native-return";
  const { Browser } = await import("@capacitor/browser");
  await Browser.open({ url: "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl), presentationStyle: "popover" });
}

export async function completeNativeGoogleSignIn(sessionId) {
  const { Browser } = await import("@capacitor/browser");
  Browser.close().catch(() => {});
  const r = await api.post("/auth/session?token_mode=1", null, { headers: { "X-Session-ID": sessionId } });
  localStorage.setItem("dp_token", r.data.token);
}

// Native Sign in with Apple (iOS). Backend verifies the identity token and returns our JWT.
export async function appleSignIn() {
  const { SignInWithApple } = await import("@capacitor-community/apple-sign-in");
  const nonce = randomStr();
  const state = randomStr();
  const { response } = await SignInWithApple.authorize({
    clientId: APP_SCHEME,
    redirectURI: process.env.REACT_APP_BACKEND_URL,
    scopes: "email name",
    state,
    nonce,
  });
  if (!response?.identityToken || (response.state && response.state !== state)) throw new Error("Apple sign-in failed");
  const r = await api.post("/auth/apple", {
    identity_token: response.identityToken,
    nonce,
    given_name: response.givenName || null,
    family_name: response.familyName || null,
  });
  localStorage.setItem("dp_token", r.data.token);
}

const TOKEN_KEY = "dp_native_push_token";
export const getSavedNativePushToken = () => localStorage.getItem(TOKEN_KEY);

// Resolves with the FCM token once the OS hands it over (or rejects on error/denied).
export async function registerNativePush({ prompt = true } = {}) {
  const { PushNotifications } = await import("@capacitor/push-notifications");
  let perm = await PushNotifications.checkPermissions();
  if (perm.receive === "prompt" && prompt) perm = await PushNotifications.requestPermissions();
  if (perm.receive !== "granted") throw new Error("Notifications are turned off for this app in Settings");
  const token = await new Promise((resolve, reject) => {
    const handles = [];
    const done = (fn, v) => { handles.forEach((h) => h.remove()); fn(v); };
    Promise.all([
      PushNotifications.addListener("registration", ({ value }) => done(resolve, value)),
      PushNotifications.addListener("registrationError", (e) => done(reject, new Error(e?.error || "Registration failed"))),
    ]).then((hs) => { handles.push(...hs); PushNotifications.register(); });
  });
  await api.post("/push/native-token", { token, platform: nativePlatform() });
  localStorage.setItem(TOKEN_KEY, token);
  return token;
}

export async function unregisterNativePush() {
  const token = getSavedNativePushToken();
  if (token) {
    try { await api.delete("/push/native-token", { params: { token } }); } catch {}
  }
  localStorage.removeItem(TOKEN_KEY);
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    await PushNotifications.unregister();
  } catch {}
}

// Writes the PNG to the app cache then opens the native share sheet.
export async function shareImageNative(blob, filename, { title, text }) {
  const { Filesystem, Directory } = await import("@capacitor/filesystem");
  const { Share } = await import("@capacitor/share");
  const base64 = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result).split(",")[1]);
    r.onerror = rej;
    r.readAsDataURL(blob);
  });
  const { uri } = await Filesystem.writeFile({ path: filename, data: base64, directory: Directory.Cache });
  await Share.share({ title, text, files: [uri] });
}
